/**
 * Tour Guide Helper / Utility
 * Manages tour guide state and user preferences
 * Can be easily extended to use API calls instead of localStorage
 */

const TOUR_STORAGE_KEYS = {
	TOUR_COMPLETED: 'tour_completed',
	TOUR_DISMISSED: 'tour_dismissed',
	TOUR_SHOWN_COUNT: 'tour_shown_count',
};

/**
 * Check if the user is new and should see the tour guide
 * Returns true unless tour was permanently completed
 * Dismissed tours show again on page reload or re-login
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 * @returns {boolean} - Returns true if tour should be shown
 */
export const shouldShowTourGuide = (isAPIEnabled = false) => {
	// Only check if tour was permanently completed
	// Dismissed tours will show again on reload/re-login
	const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED) === 'true';
	return !tourCompleted;
};

/**
 * Mark tour as completed
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 */
export const markTourAsCompleted = async (isAPIEnabled = false) => {
	if (isAPIEnabled) {
		// TODO: Replace with API call when backend is ready
		// await apiService({
		//   action: 'mark_tour_completed',
		//   timestamp: Date.now()
		// });
	}
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED, 'true');
};

/**
 * Mark tour as dismissed
 * @param {boolean} isAPIEnabled - Set to true to use API instead of localStorage
 */
export const markTourAsDismissed = async (isAPIEnabled = false) => {
	if (isAPIEnabled) {
		// TODO: Replace with API call when backend is ready
		// await apiService({
		//   action: 'dismiss_tour',
		//   timestamp: Date.now()
		// });
	}
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED, 'true');
};

/**
 * Reset tour guide (for testing/development)
 * Shows tour again from the beginning
 */
export const resetTourGuide = () => {
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED);
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED);
	localStorage.removeItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT);
	console.log('Tour guide reset. Will show on next page load.');
};

/**
 * Get tour guide status
 * @returns {object} - Returns tour status information
 */
export const getTourGuideStatus = () => {
	return {
		isCompleted: localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_COMPLETED) === 'true',
		isDismissed: localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_DISMISSED) === 'true',
		shownCount: parseInt(localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT) || '0', 10),
	};
};

/**
 * Increment tour shown count
 * Useful for analytics and deciding when to show tour again
 */
export const incrementTourShownCount = () => {
	const currentCount = parseInt(localStorage.getItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT) || '0', 10);
	localStorage.setItem(TOUR_STORAGE_KEYS.TOUR_SHOWN_COUNT, String(currentCount + 1));
};

export default {
	shouldShowTourGuide,
	markTourAsCompleted,
	markTourAsDismissed,
	resetTourGuide,
	getTourGuideStatus,
	incrementTourShownCount,
};
